import React, { useState, useEffect, useMemo } from "react";
import {
  Shield,
  Package,
  MapPin,
  AlertTriangle,
  Tag,
  Search,
  Edit2,
  QrCode,
  Barcode,
  Radio,
  Link2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import Modal from "../../../components/modal/Modal";
import axiosInstance from "../../../common/axiosInstance";
import AssetMovementTracking from "./AssetMovement";
import TheftLossReporting from "./TheftLossReporting";
import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

interface Asset {
  id: string;
  _id: string;
  name: string;
  equipmentId?: string;
  serialNumber?: string;
  equipmentInventory: string;
  unitNumber: string;
  location: string;
  floor: string;
  premisesName: string;
  region?: string;
  category?: string;
  subCategory?: string;
  status?: string;
  assetTag?: string;
  rfidTag?: string;
  barcode?: string;
  qrCode?: string;
  isRestricted: boolean;
  securityStatus: string;
  hasSecurityTag: boolean;
}

interface AssetStats {
  totalAssets: number;
  taggedAssets: number;
  untaggedAssets: number;
  restrictedAssets: number;
  todayMovements: number;
  unauthorizedToday: number;
  activeAlerts: number;
  securityEventsToday?: {
    unauthorizedMovement: number;
    assetLeftPremise: number;
    tamperDetected: number;
  };
}

const AssetSecurity: React.FC = () => {
  // const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<
    "overview" | "movement" | "theftloss" | "tagging"
  >("overview");
  const [assets, setAssets] = useState<Asset[]>([]);
  const [stats, setStats] = useState<AssetStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [tagFilter, setTagFilter] = useState<"all" | "tagged" | "untagged">(
    "all",
  );
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [tagFormData, setTagFormData] = useState({
    assetTag: "",
    rfidTag: "",
    barcode: "",
    qrCode: "",
    isRestricted: false,
    securityStatus: "Normal",
  });

  useEffect(() => {
    if (!searchQuery) {
      setDebouncedSearchQuery("");
      return;
    }

    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 350);

    return () => {
      window.clearTimeout(handle);
    };
  }, [searchQuery]);

  useEffect(() => {
    if (activeTab === "overview") {
      fetchStats();
    } else if (activeTab === "tagging") {
      fetchAssets(debouncedSearchQuery);
    }
  }, [activeTab, debouncedSearchQuery, tagFilter]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        "/api/security/asset-movements/stats",
      );
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssets = async (search?: string) => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (tagFilter === "tagged") params.tagged = "true";
      if (tagFilter === "untagged") params.tagged = "false";

      const response = await axiosInstance.get("/api/security/assets/list", {
        params,
      });
      if (response.data.success) {
        setAssets(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
      toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  const openTagModal = (asset: Asset) => {
    setSelectedAsset(asset);
    setTagFormData({
      assetTag: asset.assetTag || "",
      rfidTag: asset.rfidTag || "",
      barcode: asset.barcode || "",
      qrCode: asset.qrCode || "",
      isRestricted: asset.isRestricted || false,
      securityStatus: asset.securityStatus || "Normal",
    });
    setIsTagModalOpen(true);
  };

  const handleAssignTag = async () => {
    if (!selectedAsset) return;

    try {
      const response = await axiosInstance.post(
        `/api/security/assets/${selectedAsset.id}/assign-tag`,
        tagFormData,
      );
      if (response.data.success) {
        toast.success("Security tags assigned successfully");
        setIsTagModalOpen(false);
        setSelectedAsset(null);
        fetchAssets();
        fetchStats();
      }
    } catch (error: any) {
      console.error("Error assigning tag:", error);
      toast.error(
        error.response?.data?.message || "Failed to assign security tag",
      );
    }
  };

  const gridTheme = themeQuartz.withParams({
    backgroundColor: "#fff",
    foregroundColor: "#2a2a2a",
    headerTextColor: "#2a2a2a",
    headerBackgroundColor: "#E9F5FF",
    oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
    headerColumnResizeHandleColor: "rgb(126, 46, 132)",
    fontSize: "0.75rem",
    headerFontWeight: "bold",
  });

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Equipment Name",
        sortable: true,
        minWidth: 200,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          fontWeight: "500",
        }),
      },
      {
        field: "equipmentId",
        headerName: "Equipment ID / Serial",
        sortable: true,
        minWidth: 180,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          const asset = params.data;
          return (
            <div className="text-sm">
              <div>{asset.equipmentId || asset.equipmentInventory}</div>
              {asset.serialNumber && (
                <div className="text-xs text-gray-400">
                  S/N: {asset.serialNumber}
                </div>
              )}
            </div>
          );
        },
      },
      {
        field: "location",
        headerName: "Location",
        sortable: true,
        minWidth: 180,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          const asset = params.data;
          const region =
            asset.region && asset.region !== "N/A" ? asset.region : null;
          const premisesName =
            asset.premisesName && asset.premisesName !== "N/A"
              ? asset.premisesName
              : null;
          const location =
            asset.location && asset.location !== "N/A" ? asset.location : null;
          const floor =
            asset.floor && asset.floor !== "N/A" ? asset.floor : null;

          // Build display parts - similar to Equipment Management view
          // Format: Premises Name (bold)
          //         Region • Location - Floor (smaller, gray)
          return (
            <div className="text-sm">
              {premisesName ? (
                <div className="font-medium text-gray-900">{premisesName}</div>
              ) : (
                <div className="text-gray-400 italic">No premises</div>
              )}
              {region || location || floor ? (
                <div className="text-xs text-gray-500 mt-1">
                  {region && <span>{region}</span>}
                  {region && (location || floor) && (
                    <span className="mx-1">•</span>
                  )}
                  {location && <span>{location}</span>}
                  {location && floor && <span className="mx-1">-</span>}
                  {floor && <span>{floor}</span>}
                </div>
              ) : premisesName ? (
                <div className="text-xs text-gray-400 italic mt-1">
                  Location not specified
                </div>
              ) : null}
            </div>
          );
        },
      },
      {
        field: "hasSecurityTag",
        headerName: "Security Tags",
        sortable: true,
        minWidth: 150,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          const asset = params.data;
          if (!asset.hasSecurityTag) {
            return (
              <span className="text-xs text-gray-400">No tags assigned</span>
            );
          }
          return (
            <div className="flex flex-wrap gap-1">
              {asset.rfidTag && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                  <Tag className="w-3 h-3" /> RFID
                </span>
              )}
              {asset.barcode && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                  <Barcode className="w-3 h-3" /> Barcode
                </span>
              )}
              {asset.qrCode && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                  <QrCode className="w-3 h-3" /> QR
                </span>
              )}
            </div>
          );
        },
      },
      {
        field: "securityStatus",
        headerName: "Status",
        sortable: true,
        minWidth: 150,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          const asset = params.data;
          const statusColors: Record<string, string> = {
            Normal: "bg-green-100 text-green-800",
            "Under Surveillance": "bg-yellow-100 text-yellow-800",
            Missing: "bg-red-100 text-red-800",
            Stolen: "bg-red-100 text-red-800",
          };
          return (
            <div className="flex flex-wrap gap-1">
              <span
                className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[asset.securityStatus] ||
                  "bg-gray-100 text-gray-800"
                  }`}
              >
                {asset.securityStatus}
              </span>
              {asset.isRestricted && (
                <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                  Restricted
                </span>
              )}
            </div>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          const asset = params.data;
          return (
            <button
              onClick={() => openTagModal(asset)}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-[#0fa9a6] text-white rounded hover:bg-teal-600 transition"
            >
              <Edit2 className="w-4 h-4" />
              {asset.hasSecurityTag ? "Edit Tags" : "Assign Tags"}
            </button>
          );
        },
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    [],
  );

  const theme = useMemo(() => gridTheme, []);

  return (
    <div className="px-2 py-4 w-full h-full mt-10">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <Shield className="w-6 h-6 text-[#0fa9a6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Asset & Equipment Security
            </h1>
            <p className="text-gray-600 max-w-3xl">
              RFID/barcode tagging, movement tracking, restricted-zone and
              premise-boundary alerts, tamper events, and theft/loss workflows —
              backed by the Equipment Management inventory.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
          {[
            { icon: Radio, t: "RFID / barcode tags", d: "Linked to inventory rows" },
            { icon: MapPin, t: "Movement tracking", d: "Scan at access zones" },
            { icon: AlertTriangle, t: "Restricted alerts", d: "Zones + premise rules" },
            { icon: Shield, t: "Tamper events", d: "Manual & reader anomalies" },
            { icon: Link2, t: "Equipment module", d: "Same inventory collection" },
          ].map((x) => (
            <div
              key={x.t}
              className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm text-sm"
            >
              <x.icon className="w-5 h-5 text-[#0fa9a6] mb-1" />
              <p className="font-semibold text-gray-900">{x.t}</p>
              <p className="text-xs text-gray-500">{x.d}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-4 py-2 font-medium transition-colors ${activeTab === "overview"
                ? "border-b-2 border-[#0fa9a6] text-[#0fa9a6]"
                : "text-gray-600 hover:text-gray-900"
              }`}
          >
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Overview
            </div>
          </button>
          <button
            onClick={() => setActiveTab("tagging")}
            className={`px-4 py-2 font-medium transition-colors ${activeTab === "tagging"
                ? "border-b-2 border-[#0fa9a6] text-[#0fa9a6]"
                : "text-gray-600 hover:text-gray-900"
              }`}
          >
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Asset Tagging
            </div>
          </button>
          <button
            onClick={() => setActiveTab("movement")}
            className={`px-4 py-2 font-medium transition-colors ${activeTab === "movement"
                ? "border-b-2 border-[#0fa9a6] text-[#0fa9a6]"
                : "text-gray-600 hover:text-gray-900"
              }`}
          >
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Movement Tracking
            </div>
          </button>
          <button
            onClick={() => setActiveTab("theftloss")}
            className={`px-4 py-2 font-medium transition-colors ${activeTab === "theftloss"
                ? "border-b-2 border-[#0fa9a6] text-[#0fa9a6]"
                : "text-gray-600 hover:text-gray-900"
              }`}
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Theft/Loss Reports
            </div>
          </button>
        </div>
      </div>

      {activeTab === "movement" ? (
        <AssetMovementTracking />
      ) : activeTab === "theftloss" ? (
        <TheftLossReporting />
      ) : activeTab === "tagging" ? (
        /* Asset Tagging Tab */
        <div>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search assets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>
              </div>
              <div className="min-w-[150px]">
                <select
                  value={tagFilter}
                  onChange={(e) =>
                    setTagFilter(
                      e.target.value as "all" | "tagged" | "untagged",
                    )
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
                >
                  <option value="all">All Assets</option>
                  <option value="tagged">Tagged Only</option>
                  <option value="untagged">Untagged Only</option>
                </select>
              </div>
            </div>
          </div>

          {/* Assets List */}
          <div className="bg-white rounded-lg shadow-sm">
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <SpinLoader />
              </div>
            ) : assets.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-gray-500">
                <Package className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">No equipment found</p>
                <p className="text-sm">
                  {searchQuery
                    ? "Try adjusting your search"
                    : "Add equipment from the Equipment Management module first"}
                </p>
              </div>
            ) : (
              <div className="tableBlock w-full">
                <div className="card">
                  <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                    <AgGridReact
                      rowData={assets}
                      columnDefs={columnDefs}
                      theme={theme}
                      headerHeight={65}
                      rowHeight={50}
                      loading={loading}
                      defaultColDef={defaultColDef}
                      domLayout="autoHeight"
                      pagination={true}
                      paginationPageSize={20}
                      paginationPageSizeSelector={[10, 20, 50]}
                      paginationAutoPageSize={false}
                      getRowStyle={() => ({
                        backgroundColor: "white",
                      })}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Overview Tab */
        <div>
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <SpinLoader />
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Assets</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {stats?.totalAssets || 0}
                      </p>
                    </div>
                    <div className="bg-[#EAFBFA] p-3 rounded-lg">
                      <Package className="w-6 h-6 text-[#0fa9a6]" />
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Tagged Assets</p>
                      <p className="text-2xl font-bold text-green-600">
                        {stats?.taggedAssets || 0}
                      </p>
                    </div>
                    <div className="bg-green-100 p-3 rounded-lg">
                      <Tag className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Untagged Assets</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {stats?.untaggedAssets || 0}
                      </p>
                    </div>
                    <div className="bg-orange-100 p-3 rounded-lg">
                      <Tag className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Active Alerts</p>
                      <p className="text-2xl font-bold text-red-600">
                        {stats?.activeAlerts || 0}
                      </p>
                    </div>
                    <div className="bg-red-100 p-3 rounded-lg">
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Today's Activity */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Today's Movements
                  </h3>
                  <p className="text-3xl font-bold text-[#0fa9a6]">
                    {stats?.todayMovements || 0}
                  </p>
                  <p className="text-sm text-gray-500">
                    Asset movements detected today
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Unauthorized
                  </h3>
                  <p className="text-3xl font-bold text-red-600">
                    {stats?.unauthorizedToday || 0}
                  </p>
                  <p className="text-sm text-gray-500">
                    Unauthorized movements today
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Restricted Assets
                  </h3>
                  <p className="text-3xl font-bold text-orange-600">
                    {stats?.restrictedAssets || 0}
                  </p>
                  <p className="text-sm text-gray-500">
                    Assets with movement restrictions
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Asset security events (today)
                </h3>
                <p className="text-sm text-gray-500 mb-3">
                  Unauthorized movement, asset left premise (restricted item
                  scanned outside home premise vs access zone), and tamper
                  detection.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-red-500">
                    <p className="text-sm text-gray-600">Unauthorized movement</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.securityEventsToday?.unauthorizedMovement ?? 0}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-amber-500">
                    <p className="text-sm text-gray-600">Left premise</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.securityEventsToday?.assetLeftPremise ?? 0}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-purple-600">
                    <p className="text-sm text-gray-600">Tamper detected</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.securityEventsToday?.tamperDetected ?? 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Quick Actions
                </h3>
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => setActiveTab("tagging")}
                    className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full hover:bg-teal-600"
                  >
                    <Tag className="w-5 h-5" />
                    Assign Security Tags
                  </button>
                  <button
                    onClick={() => setActiveTab("movement")}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700"
                  >
                    <MapPin className="w-5 h-5" />
                    Track Movements
                  </button>
                  <button
                    onClick={() => setActiveTab("theftloss")}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700"
                  >
                    <AlertTriangle className="w-5 h-5" />
                    Report Theft/Loss
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tag Assignment Modal */}
      <Modal
        isOpen={isTagModalOpen}
        onClose={() => {
          setIsTagModalOpen(false);
          setSelectedAsset(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-lg rounded-xl shadow-lg relative flex flex-col max-h-[85vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            Assign Security Tags
          </h2>
          <div className="px-6 pb-6">
            {selectedAsset && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900">
                  {selectedAsset.name}
                </p>
                <p className="text-sm text-gray-600">
                  {selectedAsset.equipmentId &&
                    `ID: ${selectedAsset.equipmentId}`}
                  {selectedAsset.serialNumber &&
                    ` | S/N: ${selectedAsset.serialNumber}`}
                </p>
                <p className="text-xs text-gray-400">
                  {selectedAsset.region &&
                    selectedAsset.region !== "N/A" &&
                    `${selectedAsset.region} • `}
                  {selectedAsset.premisesName &&
                    selectedAsset.premisesName !== "N/A"
                    ? selectedAsset.premisesName
                    : "No premises"}
                  {selectedAsset.location &&
                    selectedAsset.location !== "N/A" &&
                    ` • ${selectedAsset.location}`}
                  {selectedAsset.floor &&
                    selectedAsset.floor !== "N/A" &&
                    ` - ${selectedAsset.floor}`}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  <Tag className="w-4 h-4 inline mr-1" /> Asset Tag (Custom ID)
                </label>
                <input
                  type="text"
                  value={tagFormData.assetTag}
                  onChange={(e) =>
                    setTagFormData({ ...tagFormData, assetTag: e.target.value })
                  }
                  placeholder="e.g., AST-001"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  <Tag className="w-4 h-4 inline mr-1" /> RFID Tag
                </label>
                <input
                  type="text"
                  value={tagFormData.rfidTag}
                  onChange={(e) =>
                    setTagFormData({ ...tagFormData, rfidTag: e.target.value })
                  }
                  placeholder="Scan or enter RFID tag"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  <Barcode className="w-4 h-4 inline mr-1" /> Barcode
                </label>
                <input
                  type="text"
                  value={tagFormData.barcode}
                  onChange={(e) =>
                    setTagFormData({ ...tagFormData, barcode: e.target.value })
                  }
                  placeholder="Scan or enter barcode"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  <QrCode className="w-4 h-4 inline mr-1" /> QR Code
                </label>
                <input
                  type="text"
                  value={tagFormData.qrCode}
                  onChange={(e) =>
                    setTagFormData({ ...tagFormData, qrCode: e.target.value })
                  }
                  placeholder="Enter QR code value"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Security Status
                </label>
                <select
                  value={tagFormData.securityStatus}
                  onChange={(e) =>
                    setTagFormData({
                      ...tagFormData,
                      securityStatus: e.target.value,
                    })
                  }
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="Normal">Normal</option>
                  <option value="Under Surveillance">Under Surveillance</option>
                  <option value="Restricted">Restricted</option>
                  <option value="Missing">Missing</option>
                  <option value="Stolen">Stolen</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isRestricted"
                  checked={tagFormData.isRestricted}
                  onChange={(e) =>
                    setTagFormData({
                      ...tagFormData,
                      isRestricted: e.target.checked,
                    })
                  }
                  className="w-4 h-4 text-[#0fa9a6] focus:ring-[#0fa9a6] border-gray-300 rounded"
                />
                <label
                  htmlFor="isRestricted"
                  className="text-sm font-medium text-gray-700"
                >
                  Restrict asset movement (cannot leave premises)
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-gray-500 transition"
            onClick={() => {
              setIsTagModalOpen(false);
              setSelectedAsset(null);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAssignTag}
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Save Tags
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AssetSecurity;
